'use client'

// Re-export from store configuration
import {
  persistor,
  store,
  type AppDispatch,
  type RootState
} from './store'

// Re-export hooks
import { useSession } from 'next-auth/react'
import { useEffect } from 'react'

import { clearLastVehicle } from './features/booking/lastVehicleSlice'

// Re-export typed hooks from dedicated hooks file
export { useAppDispatch, useAppSelector } from './hooks'

// Track who is signed in so shared-device memory (the remembered vehicle) is
// dropped on logout / user-switch. Booking state itself is NOT purged here: its
// lifecycle is owner-gated on /book (see src/app/book/lib/booking-owner-gate.ts),
// so a logged-out owner can be prompted to sign in and resume on return rather
// than silently losing their in-flight booking.
export const useStoreUserSync = () => {
  const { data: session, status } = useSession()

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Don't act until the session is resolved — acting on the transient
    // 'loading' state would misread every refresh as a logout.
    if (status === 'loading') return

    const userKey = session?.user?.id ? `user-${session.user.id}` : 'anonymous'
    const previousKey = localStorage.getItem('lastUserKey')

    if (previousKey === userKey) return

    // A real user is being replaced by someone else (logout or user-to-user
    // switch). Drop the remembered vehicle from memory so a shared device never
    // shows the previous user's vehicle. The account copy (User.lastBookingVehicle)
    // is untouched. Anonymous→user (finishing a booking started while logged out)
    // is preserved. The initial null→key seed also falls through here as a no-op.
    if (previousKey && previousKey !== 'anonymous') {
      store.dispatch(clearLastVehicle())
    }

    localStorage.setItem('lastUserKey', userKey)
  }, [session, status])
}

// Re-export selectors
export * from './selectors'

// Re-export store and types
export { persistor, store }
export type { AppDispatch, RootState }

